import {ICheatPluginController, ICheatsModule} from '../../CheatModuleInterfaces';
import {CommandLineInterface, consoleLogger, CoreDI, lazyInject} from '@gongxigames/framework/core';
import {IEventManagerWithAsync} from '@gongxigames/framework/core/types';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {AbstractFilter, NoBonusFilter, NoWinFilter, WinTypeFilter} from '@gongxigames/framework/slot/services/logic';
import {WIN_TYPES} from '../../CheatsConstants';

type FiltersByGroup = Record<string, AbstractFilter[]>;

export class CheatFiltersController implements ICheatPluginController {
    public static filtersByGroup: FiltersByGroup = {};
    protected filters: AbstractFilter[] = [];
    protected module!: ICheatsModule;

    @lazyInject(CoreDI.GameConfig)
    protected readonly gameConfig!: GameConfig;

    @lazyInject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    @lazyInject(CoreDI.IEventManagerWithAsync)
    private _eventManager!: IEventManagerWithAsync;

    public static addFilters(filters: AbstractFilter[], groupName = ''): void {
        if (!this.filtersByGroup[groupName]) {
            this.filtersByGroup[groupName] = [];
        }

        this.filtersByGroup[groupName].push(...filters);
    }

    public onChanged: Function = (): void => {};

    public init(module: ICheatsModule): void {
        this.module = module;
        this.module.storage.on('cheatActions', () => this.onChanged(), this);

        CheatFiltersController.addFilters([
            new NoWinFilter('NoWin'),
            new NoBonusFilter('NoBonus')
        ]);

        // setParameters flow filters
        const {winTypes} = this.gameConfig.winConfig.normal;

        const winFilters: any[] = WIN_TYPES.map(winType => new WinTypeFilter(winType, winTypes));

        CheatFiltersController.addFilters(winFilters, 'Win Type Filters');

        this.filters = [];

        Object.values(CheatFiltersController.filtersByGroup).forEach(filters => {
            this.filters.push(...filters);
        });

        this._cli.addCommand('selectFilter', this.selectFilter.bind(this));
        this._cli.addCommand('removeFilters', this.removeFilters.bind(this));
        this._cli.addCommand('getFilters', () => {
            this.filters.forEach(({name}) => consoleLogger.log(name));
        });

        this._eventManager.on('CheatModule.addFilters', this._onAddFilters.bind(this), this);
        this._eventManager.on('CheatModule.removeFilters', this._onRemoveFilters.bind(this), this);
        this._eventManager.on('CheatModule.clearFilters', this._onClearFilters.bind(this), this);

        // For AF: pointer when filters are ready and can be set.
        this._eventManager.emit('CheatModule.initialized');
    }

    public get filtersByGroup(): FiltersByGroup {
        return CheatFiltersController.filtersByGroup;
    }

    public get selectedFilters(): string[] {
        const {cheatActions} = this.module.storage.data;

        return this.filters
            .filter(filter => cheatActions.find( act => act.id === filter.name))
            .map(({name}) => name);
    }

    public toggleFilter(filterName: string): void {
        const filter = this.filters.find(filter => filter.name === filterName);

        if (filter) {
            const action = this._getFilterAction(filter);

            if (this.module.storage.data.cheatActions.find(a => a.id === action.id)) {
                this.module.removeCheatAction(action);
            } else {
                this.module.addCheatAction(action);
            }
        }
    }

    protected removeFilters(): void {
        this.selectedFilters.forEach((filterName) => this.removeFilterByName(filterName));
    }

    protected selectFilter(filterName: string): void {
        this.addFilterByName(filterName);
    }

    protected addFilterByName(filterName: string): void {
        const filter = this.filters.find(filter => filter.name === filterName);

        if (filter) {
            const action = this._getFilterAction(filter);

            if (!this._isSelected(action.id)) {
                this.module.addCheatAction(action);
            }
        }
    }

    protected removeFilterByName(filterName: string): void {
        const filter = this.filters.find(filter => filter.name === filterName);

        if (filter) {
            const action = this._getFilterAction(filter);

            if (this._isSelected(action.id)) {
                this.module.removeCheatAction(action);
            }
        }
    }

    private _onAddFilters(filterNames: string | string[]): void {
        [...filterNames].forEach(filterName => {
            this.addFilterByName(filterName);
        });
    }

    private _onRemoveFilters(filterNames: string | string[]): void {
        [...filterNames].forEach(filterName => {
            this.removeFilterByName(filterName);
        });
    }

    private _onClearFilters(): void {
        this.removeFilters();
    }

    private _isSelected(filterName: string): boolean {
        return !!this.module.storage.data.cheatActions.find(a => a.id === filterName);
    }

    private _getFilterAction(filter: AbstractFilter): { id: string; apply: () => void; reject: () => void;} {
        return {
            id: filter.name,
            apply: () => this.module.addArrayFilter(filter),
            reject: () => this.module.removeArrayFilter(filter)
        };
    }
}
